import type { AuthProviderProps } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";

/**
 * OIDC / Keycloak configuration for the NellsPay Customer Web Client.
 *
 * These values mirror the Keycloak settings in NellsPay.Web appsettings.json
 * (Users:KeyCloak — realm "nellspay", public client "np-public-client").
 *
 * This is a PUBLIC client using Authorization Code + PKCE. There is NO
 * client_secret in the config or in the bundle.
 *
 * Environment variables (prefixed VITE_) can override defaults at build time.
 */

const keycloakBaseUrl =
  import.meta.env.VITE_KEYCLOAK_BASE_URL ??
  "https://identity-dev.nellspay.com";

// Customer realm is "nellspay" (NOT the admin "nellspay-admin").
const realm = import.meta.env.VITE_KEYCLOAK_REALM ?? "nellspay";

/** The real (absolute) authority — used for issuer validation and browser redirects. */
const authority = `${keycloakBaseUrl}/realms/${realm}`;

/**
 * Proxy prefix used in development so XHR/fetch calls to Keycloak go through the
 * Vite dev-server proxy and avoid browser CORS restrictions.
 * In production nginx rewrites /idp → the real IdP.
 */
const idpProxy = `/idp/realms/${realm}/protocol/openid-connect`;

// Public Keycloak client. No secret is bundled (PKCE only).
const clientId =
  import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? "np-public-client";

const redirectUri =
  import.meta.env.VITE_REDIRECT_URI ?? `${window.location.origin}/callback`;

const postLogoutRedirectUri =
  import.meta.env.VITE_POST_LOGOUT_REDIRECT_URI ?? window.location.origin;

// Must point to the STATIC file in /public, not a SPA route.
// If this is set to "/silent-renew" (no extension) Vite/nginx serve index.html
// (SPA fallback), which loads the full React app inside the renewal iframe.
// The iframe has its own empty sessionStorage so oidc-client-ts cannot find
// the code_verifier and throws "No matching state found in storage".
const silentRedirectUri =
  import.meta.env.VITE_SILENT_REDIRECT_URI ??
  `${window.location.origin}/silent-renew.html`;

export const oidcConfig: AuthProviderProps = {
  // authority is the real Keycloak URL — oidc-client-ts uses it to validate the
  // "iss" claim in tokens.
  authority,
  client_id: clientId,
  // PUBLIC client → no client_secret.
  redirect_uri: redirectUri,
  post_logout_redirect_uri: postLogoutRedirectUri,
  silent_redirect_uri: silentRedirectUri,
  // Authorization Code flow with PKCE (oidc-client-ts enables PKCE for
  // response_type "code" automatically).
  response_type: "code",
  scope: "openid profile email",
  automaticSilentRenew: true,
  // Start the renewal 60 seconds before the access token expires so API calls
  // never hit the backend with a stale token.
  accessTokenExpiringNotificationTimeInSeconds: 60,
  // The id_token already carries the needed claims (openid, profile, email).
  // Disabling the separate userinfo call avoids issues with CDN/AFD proxying and
  // eliminates an unnecessary network round-trip on every sign-in.
  loadUserInfo: false,

  // Provide the OIDC metadata directly so the library does NOT need to fetch the
  // discovery document (which would be a cross-origin request blocked by CORS).
  // • Browser-navigation endpoints (auth, logout) → absolute Keycloak URLs (no CORS).
  // • XHR endpoints (token, userinfo, jwks, revoke) → relative /idp proxy paths.
  metadata: {
    issuer: authority,
    authorization_endpoint: `${authority}/protocol/openid-connect/auth`,
    end_session_endpoint: `${authority}/protocol/openid-connect/logout`,
    token_endpoint: `${idpProxy}/token`,
    userinfo_endpoint: `${idpProxy}/userinfo`,
    jwks_uri: `${idpProxy}/certs`,
    revocation_endpoint: `${idpProxy}/revoke`,
  },

  // Revoke access/refresh tokens at the IdP on signout so they cannot be
  // replayed after logout (revocation_endpoint is wired in metadata above).
  revokeTokensOnSignout: true,

  // Use sessionStorage so tokens do not persist across browser restarts.
  // Trade-off: the user must sign in again after closing the browser, but
  // long-lived tokens are not left in localStorage where any XSS payload
  // could exfiltrate them at leisure. In-tab refreshes still keep the session.
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  // Let stateStore default to sessionStorage — transient OIDC state (nonce, code_verifier)
  // is only needed for the duration of one redirect round-trip and should not persist.
};

/**
 * Keycloak realm and client metadata (for use outside the OIDC provider).
 * Mirrors Users:KeyCloak section from NellsPay.Web appsettings.json.
 */
export const keycloakMeta = {
  realm,
  authority,
  clientId,
  tokenUrl: `${authority}/protocol/openid-connect/token`,
  issuer: authority,
  metadataAddress: `${authority}/.well-known/openid-configuration`,
} as const;
